import React from 'react';
import { Box, Container, Typography, Button, Paper, useTheme } from '@mui/material';
import { Construction, ArrowBack, Schedule } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@emotion/react';
import { MENU_DATA } from '@core/data/menuData.ts';
import { FCSecurePage } from '@/components/shared/navigation/securePage.ts';

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

// Helper function to find menu item by path
const findMenuItemByPath = (path: string): any => {
    const searchInItems = (items: any[], targetPath: string): any => {
        for (const item of items) {
            if (item.path === targetPath) {
                return item;
            }
            if (item.items) {
                const found = searchInItems(item.items, targetPath);
                if (found) return found;
            }
        }
        return null;
    };

    for (const category of Object.values(MENU_DATA)) {
        const found = searchInItems(category.items, path);
        if (found) return found;
    }
    return null;
};

// Generate title from menu item or path
const generateTitle = (menuItem: any, path: string, t: (key: string) => string): string => {
    if (menuItem?.title) {
        return t(menuItem.title);
    }

    // Generate title from path
    const pathParts = path.split('/').filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];

    // Convert camelCase or path segments to readable titles
    const formatTitle = (str: string) => {
        return str
            .replace(/([A-Z])/g, ' $1') // Add space before capitals
            .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
            .trim();
    };

    return formatTitle(lastPart) || 'Feature Coming Soon';
};

export const ComingSoon: FCSecurePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const theme = useTheme();

    // Find menu item based on current path
    const menuItem = findMenuItemByPath(location.pathname);

    // Auto-generate title
    const title = generateTitle(menuItem, location.pathname, t);

    const handleGoHome = () => {
        navigate('/myshops');
    };

    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Paper
                elevation={8}
                sx={{
                    p: { xs: 4, md: 6 },
                    textAlign: 'center',
                    borderRadius: '0',
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)'
                            : 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
                    border: `1px solid ${theme.palette.divider}`,
                    animation: `${fadeIn} 0.6s ease-out`
                }}
            >
                {/* Icon */}
                <Box sx={{ mb: 3 }}>
                    <Construction
                        sx={{
                            fontSize: '80px',
                            color: 'primary.main'
                        }}
                    />
                </Box>

                {/* Title */}
                <Typography
                    variant="h4"
                    component="h1"
                    gutterBottom
                    sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 2,
                        fontSize: { xs: '1.75rem', md: '2.25rem' }
                    }}
                >
                    {title}
                </Typography>

                {/* Status Badge */}
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 3,
                        py: 1.5,
                        mb: 4,
                        mt: 4,
                        backgroundColor:
                            theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                        borderRadius: '25px',
                        border: `1px solid ${theme.palette.primary.main}30`
                    }}
                >
                    <Schedule sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}
                    >
                        {'Feature Coming Soon'}
                    </Typography>
                </Box>

                {/* Action Buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'center',
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}
                >
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<ArrowBack />}
                        onClick={handleGoHome}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderRadius: '0 !important',
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            minWidth: '140px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                            }
                        }}
                    >
                        {t('shop-control-center') || 'Dashboard'}
                    </Button>
                </Box>

                {/* Current Path Info for Development */}
                {process.env.NODE_ENV === 'development' && (
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            mt: 3,
                            color: 'text.disabled',
                            fontSize: '0.8rem',
                            fontFamily: 'Wanzl_Etelka Light, monospace'
                        }}
                    >
                        Path: {location.pathname}
                    </Typography>
                )}
            </Paper>
        </Container>
    );
};

ComingSoon.displayName = 'ComingSoon';
export default ComingSoon;
